
import { useState, useEffect, useRef, useCallback } from 'react';

export default function PortfolioSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);

  const projects = [
    {
      title: '강동sk리더스뷰 아트리움',
      location: '서울시 강동구 길동 415-9',
      status: '분양중',
      image:
        'https://static.readdy.ai/image/0adaafd12e96644bb3f30378f0b23f95/894fd8fd4edf90f992b1c370ed6e15ac.jpeg',
    },
    {
      title: '부발역 에피트 에디션',
      location: '경기도 이천시 부발읍 아미리 산 111번지 일원',
      status: '분양마감',
      image:
        'https://static.readdy.ai/image/0adaafd12e96644bb3f30378f0b23f95/bd7d80f8ca0e1e477632481cb6410522.jpeg',
    },
    {
      title: '신광교 클라우드시티',
      location: '경기 용인시 기흥구 영덕동 1336',
      status: '분양중',
      image:
        'https://static.readdy.ai/image/0adaafd12e96644bb3f30378f0b23f95/01233893a94e287e2c74e64b9c07a257.jpeg',
    },
    {
      title: '동탄 힐스테이트 르센텀',
      location: '경기 화성시 동탄구 영천동 858',
      status: '분양마감',
      image:
        'https://static.readdy.ai/image/0adaafd12e96644bb3f30378f0b23f95/b0a8777ea14ae2546d62dd70b9dc4e50.jpeg',
    },
    {
      title: '군포 트리아츠',
      location: '경기도 군포시 당동 150-1번지',
      status: '분양마감',
      image:
        'https://static.readdy.ai/image/0adaafd12e96644bb3f30378f0b23f95/1b8b9af46d77d4fc0a18f9af97c6a79d.jpeg',
    },
    {
      title: '시흥시청역 현대테라타워',
      location: '경기도 시흥시 광석동 505',
      status: '분양마감',
      image:
        'https://static.readdy.ai/image/0adaafd12e96644bb3f30378f0b23f95/039987eb9eba226ae1696012469e9458.jpeg',
    },
  ];

  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const itemsPerView = isMobile ? 1 : 3;
  const totalSlides = Math.ceil(projects.length / itemsPerView);
  const maxIndex = totalSlides - 1;

  // Keep currentIndex within bounds when total slides change
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [maxIndex, currentIndex]);

  // Auto‑play logic
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, maxIndex]);

  const handlePrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  // Drag handling (mouse only – touch can be added similarly)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setDragOffset(0);
    setIsAutoPlaying(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      const diff = e.clientX - startX;
      setDragOffset(diff);
    },
    [isDragging, startX],
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (Math.abs(dragOffset) > 80) {
      if (dragOffset < 0) {
        setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
      } else {
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
      }
    }
    setDragOffset(0);
  }, [isDragging, dragOffset, maxIndex]);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setDragOffset(0);
    }
  }, [isDragging]);

  const getSliderTransform = () => {
    const baseTranslate = currentIndex * 100;
    const containerWidth = sliderRef.current?.offsetWidth || 1200;
    const dragPercent = (dragOffset / containerWidth) * 100;
    return `translateX(-${baseTranslate - dragPercent}%)`;
  };

  return (
    <section id="portfolio" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-end justify-between mb-12" data-aos="fade-up">
          <div>
            <p className="text-primary text-sm font-semibold tracking-wider uppercase mb-3">
              Business Performance
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-secondary font-display">
              한성<span className="font-normal">汗成</span> 사업실적
            </h2>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={handlePrev}
              className="w-12 h-12 flex items-center justify-center rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
            <button
              onClick={handleNext}
              className="w-12 h-12 flex items-center justify-center rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 cursor-pointer"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Slider */}
        <div
          ref={sliderRef}
          className="relative overflow-hidden select-none"
          data-aos="fade-up"
          data-aos-delay="100"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className={`flex ${isDragging ? '' : 'transition-transform duration-500 ease-out'}`}
            style={{ transform: getSliderTransform() }}
          >
            {Array.from({ length: totalSlides }).map((_, slideIndex) => (
              <div key={slideIndex} className="flex-shrink-0 w-full">
                <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
                  {projects
                    .slice(slideIndex * itemsPerView, slideIndex * itemsPerView + itemsPerView)
                    .map((project, idx) => (
                      <div
                        key={idx}
                        className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer"
                      >
                        <div className={`relative ${isMobile ? 'h-[520px]' : 'h-[380px]'} overflow-hidden`}>
                          <img
                            src={project.image}
                            alt={project.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                            draggable={false}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
                          <div className="absolute top-4 right-4">
                            <span
                              className={`px-4 py-1.5 rounded-full text-xs font-semibold shadow-md ${
                                project.status === '분양중' ? 'bg-primary text-white' : 'bg-gray-600 text-white'
                              }`}
                            >
                              {project.status}
                            </span>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-6">
                            <h3 className="text-xl font-bold text-white mb-1.5">{project.title}</h3>
                            <div className="flex items-center gap-1.5 text-white/80 text-sm">
                              <i className="ri-map-pin-line text-primary"></i>
                              <p>{project.location}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile navigation buttons */}
        <div className="flex md:hidden items-center justify-center gap-4 mt-8">
          <button
            onClick={handlePrev}
            className="w-12 h-12 flex items-center justify-center rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
          <button
            onClick={handleNext}
            className="w-12 h-12 flex items-center justify-center rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>

        {/* Pagination dots */}
        <div className="flex justify-center mt-8">
          <div className="flex gap-2.5">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsAutoPlaying(false);
                  setCurrentIndex(index);
                }}
                className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                  currentIndex === index ? 'w-10 bg-primary' : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                }`}
              ></button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
